<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {ClientJS} from "clientjs";
import useConnector from "./ConnectorComposition";
import SwitchModal from "./SwitchModal.vue";
import PostContainerDetail from "./PostContainerDetail.vue";
import ErrorsArea from "./ErrorsArea.vue";
import ConfirmModal from "./ConfirmModal.vue";
import SnsShareModal from "./SnsShareModal.vue";
import PostContainerSelf from "./PostContainerSelf.vue";
import {nextTick} from "alpinejs/src/nextTick";

const siteUrl = document.querySelector('meta[name="site-url"]').content;
const props = defineProps({
  post: {
    type: Object,
    required: true,
  },
  loginUserId: {
    type: Number,
    required: true,
  },
  containerType: {
    type: String,
    required: true,
  },
});
const emits = defineEmits(['update-post']);
const _connector = useConnector();

// 共感の状態
const empathy = ref(Boolean(props.post.is_empathy));

// お気に入りの状態
const favorite = ref(Boolean(props.post.is_favorite));

// 違反の状態
const violation = ref(Boolean(props.post.is_violation));

// 公開状態
const publish = ref(Number(props.post.status) === 1);

// コメント受付設定
const acceptComment = ref(Number(props.post.accept_comments_type) === 1);

// メニュー欄が開いているかどうか
const isOpenMenu = ref(false);

// モーダルの表示状態
const isShowPublishModal = ref(false);
const isShowCommentSettingModal = ref(false);
const isShowViolationEnableModal = ref(false);
const isShowViolationDisableModal = ref(false);
const isShowDeleteReadyModal = ref(false);
const isShowDeleteCompleteModal = ref(false);
const isShowSnsShareModal = ref(false);

// ロック
const lock = ref(false);

// 投稿日時を Date オブジェクトに変換
const createdAt = computed(() => new Date(props.post.created_at * 1000));

// New マークを表示するかどうか (259200000 は 3日)
const isNew = computed(() => {
  const now = new Date();
  const diff = now.getTime() - createdAt.value.getTime();
  return diff < 259200000;
});

// 誕生日から何十代かを計算
const decade = computed(() => {
  const birthday = new Date(props.post.user.birthday * 1000);
  const now = new Date();
  const age = now.getFullYear() - birthday.getFullYear();
  return Math.floor(age / 10) * 10;
});

//本文を改行ごとに配列化して返す
const contentParagraph = computed(() => props.post.content.replace(/\r\n/g, "\n").split("\n"));

// 共感数を計算
const empathyCount = computed(() => {
  // 初期状態が共感していないかつ、現在は共感済みの場合は+1
  if (empathy.value && ! props.post.is_empathy) {
    return props.post.count_empathy + 1;
  }

  // 初期状態が共感済みかつ、現在は共感していない場合は-1
  if ( ! empathy.value && props.post.is_empathy) {
    return props.post.count_empathy - 1;
  }

  // その他の場合はそのまま
  return props.post.count_empathy;
});

// この投稿が自分のものかどうか
const isMyPost = computed(() => Number(props.post.user.id) === Number(props.loginUserId));

/**
 * 共感をトグルさせる
 * @param booleanValue
 * @returns {Promise<boolean>}
 */
const toggleEmpathy = async (booleanValue) => {
  // ロック中なら何もしない
  if (lock.value) {
    return false;
  }

  // ロック
  lock.value = true;

  // フィンガープリントを取得
  const body = new FormData();
  body.append('fingerprint', (new ClientJS()).getFingerprint());

  //サーバーに依頼
  const result = await _connector.connectPost(booleanValue ? siteUrl+'place/set-empathy/'+props.post.id : siteUrl+'place/delete-empathy/'+props.post.id, body);

  // ロック解除
  lock.value = false;

  //失敗したらメッセージアラートを表示
  if (result === null) {
    //アラート表示
    if ( ! _connector.isDoubleConnectBlock.value) {
      alert(_connector.errors.value.join("\n"));
    }

    //失敗を返す
    return false;
  }

  //成功したらローカルの値も変更する
  empathy.value = booleanValue;

  // 更新があったら親に通知
  emits('update-post');

  //成功を返す
  return true;
};

/**
 * お気に入りをトグルさせる
 * @returns {Promise<boolean>}
 */
const toggleFavorite = async (booleanValue) => {
  //サーバーに依頼
  const result = await _connector.connectPost(booleanValue ? siteUrl+'place/set-favorite/'+props.post.id : siteUrl+'place/delete-favorite/'+props.post.id);

  //失敗したらメッセージアラートを表示
  if (result === null) {
    //アラート表示
    if ( ! _connector.isDoubleConnectBlock.value) {
      alert(_connector.errors.value.join("\n"));
    }

    //失敗を返す
    return false;
  }

  //成功したらローカルの値も変更する
  favorite.value = booleanValue;

  // 更新があったら親に通知
  emits('update-post');

  //成功を返す
  return true;
};

/**
 * 違反をトグルさせる
 * @param booleanValue
 * @returns {Promise<boolean>}
 */
const toggleViolation = async (booleanValue) => {
  //サーバーに依頼
  const result = await _connector.connectPost(booleanValue ? siteUrl+'place/set-violation/'+props.post.id : siteUrl+'place/delete-violation/'+props.post.id);

  //失敗したらメッセージアラートを表示
  if (result === null) {
    //アラート表示
    if ( ! _connector.isDoubleConnectBlock.value) {
      alert(_connector.errors.value.join("\n"));
    }

    //失敗を返す
    return false;
  }

  //成功したらローカルの値も変更する
  violation.value = booleanValue;

  // 違反報告後なら違反報告解除実行モーダルを表示
  if ( ! booleanValue) {
    isShowViolationEnableModal.value = false;
    isShowViolationDisableModal.value = true;
  }

  // 違反報告解除後なら違反報告解除完了モーダルを表示
  else {
    isShowViolationDisableModal.value = false;
    isShowViolationEnableModal.value = true;
  }

  // 更新があったら親に通知
  emits('update-post');

  //成功を返す
  return true;
};

/**
 * 公開状態を変更する
 * @param booleanValue
 * @returns {Promise<void>}
 */
const changePublish = async (booleanValue) => {
  //通知 body を生成
  const formData = new FormData();
  formData.append('status', booleanValue ? '1' : '3');

  //通知を試みる
  try {
    //更新
    const result = await _connector.connectPost(siteUrl+'mypage/change-status/'+props.post.id, formData);

    //更新成功
    if (result !== null) {
      // 値を変更
      publish.value = booleanValue;

      // 更新があったら親に通知
      emits('update-post');

      // モーダルを閉じる
      isShowPublishModal.value = false;
    }
  }

    //なんらかが失敗
  catch (messages) {
    console.log(messages);
  }
};

/**
 * コメント受付設定を変更する
 * @param booleanValue
 * @returns {Promise<void>}
 */
const changeCommentSetting = async (booleanValue) => {
  //通知 body を生成
  const formData = new FormData();
  formData.append('accept_comments_type', booleanValue ? '1' : '0');

  //通知を試みる
  try {
    //更新
    const result = await _connector.connectPost(siteUrl+'mypage/accept-comment/'+props.post.id, formData);

    //更新成功
    if (result !== null) {
      // 値を変更
      acceptComment.value = booleanValue;

      // 更新があったら親に通知
      emits('update-post');

      // モーダルを閉じる
      isShowCommentSettingModal.value = false;
    }
  }

    //なんらかが失敗
  catch (messages) {
    console.log(messages);
  }
};

/**
 * 投稿を削除する
 * @returns {Promise<void>}
 */
const changeDelete = async () => {
  //通知を試みる
  try {
    //更新
    const result = await _connector.connectPost(siteUrl+'mypage/delete-post/'+props.post.id, null);

    // 更新成功
    if (result !== null) {
      // モーダルを閉じる
      isShowDeleteReadyModal.value = false;

      // 更新があったら親に通知
      emits('update-post');

      // 削除完了モーダルを表示
      isShowDeleteCompleteModal.value = true;
    }
  }

    //なんらかが失敗
  catch (messages) {
    console.log(messages);
  }
};

// 画面遷移を経てもメニューが開いたままにならないようにする
onMounted(() => {
  isOpenMenu.value = false;
  nextTick(() => {
    isOpenMenu.value = false;
  });
});

// 描画する投稿が変わったら変動する可能性のある情報を DB 由来の情報に更新
watch(() => props.post.id, () => {
  empathy.value = Boolean(props.post.is_empathy);
  favorite.value = Boolean(props.post.is_favorite);
  violation.value = Boolean(props.post.is_violation);
  publish.value = Number(props.post.status) === 1;
  acceptComment.value = Number(props.post.accept_comments_type) === 1;
});
</script>

<template>
  <div>
    <PostContainerDetail
      v-if="containerType === 'detail'"
      :is-detail-mode="true"
      :is-my-post="isMyPost"
      :empathy-count="empathyCount"
      :content-paragraph="contentParagraph"
      :decade="decade"
      :is-new="isNew"
      :created-at="createdAt"
      :is-open-menu="isOpenMenu"
      :violation="violation"
      :favorite="favorite"
      :empathy="empathy"
      :login-user-id="loginUserId"
      :post="post"
      :is-login="loginUserId !== 0"
      @toggle-favorite="toggleFavorite"
      @toggle-empathy="toggleEmpathy"
      @toggle-violation="toggleViolation"
      @show-publish-modal="isShowPublishModal = true"
      @show-comment-setting-modal="isShowCommentSettingModal = true"
      @open-menu="isOpenMenu = true"
      @close-menu="isOpenMenu = false"
    />
    <PostContainerDetail
      v-if="containerType === 'list'"
      :is-detail-mode="false"
      :is-my-post="isMyPost"
      :empathy-count="empathyCount"
      :content-paragraph="contentParagraph"
      :decade="decade"
      :is-new="isNew"
      :created-at="createdAt"
      :is-open-menu="isOpenMenu"
      :violation="violation"
      :favorite="favorite"
      :empathy="empathy"
      :login-user-id="loginUserId"
      :post="post"
      :is-login="loginUserId !== 0"
      @toggle-favorite="toggleFavorite($event)"
      @toggle-empathy="toggleEmpathy($event)"
      @toggle-violation="toggleViolation($event)"
      @show-publish-modal="isShowPublishModal = true"
      @show-comment-setting-modal="isShowCommentSettingModal = true"
      @open-menu="isOpenMenu = true"
      @close-menu="isOpenMenu = false"
    />
    <PostContainerSelf
      v-if="containerType === 'self'"
      :empathy-count="empathyCount"
      :created-at="createdAt"
      :is-open-menu="isOpenMenu"
      :post="post"
      @close-menu="isOpenMenu = false"
      @open-menu="isOpenMenu = true"
      @show-publish-modal="isShowPublishModal = true"
      @show-comment-setting-modal="isShowCommentSettingModal = true"
      @show-delete-modal="isShowDeleteReadyModal = true"
      @show-sns-share-modal="isShowSnsShareModal = true"
    />
    <teleport to="body">
      <SwitchModal
        :boolean-value="publish"
        :is-show="isShowPublishModal"
        disable-text="非公開"
        enable-text="公開"
        switch-text="公開設定"
        title-text="公開・非公開の設定ができます。"
        header-text="公開設定"
        @request-close="isShowPublishModal = false"
        @request-submit="changePublish"
      >
        <p class="pFont14">公開設定</p>
        <ErrorsArea :messages="_connector.errors" />
      </SwitchModal>
    </teleport>
    <teleport to="body">
      <SwitchModal
        :boolean-value="acceptComment"
        :is-show="isShowCommentSettingModal"
        disable-text="いらない"
        enable-text="ほしい"
        switch-text="みんなからのコメント"
        title-text="みんなからのコメントを受け取るか、受け取らないか設定できます。"
        header-text="みんなからのコメント設定"
        @request-close="isShowCommentSettingModal = false"
        @request-submit="changeCommentSetting"
      >
        <p class="pFont14">みんなからのコメント</p>
        <ErrorsArea :messages="_connector.errors" />
      </SwitchModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="isShowViolationEnableModal"
        title-text="違反報告"
        submit-text="違反報告を取り消す"
        @request-close="isShowViolationEnableModal = false"
        @request-submit="toggleViolation(false)"
      >
        <p class="lyMb16 pFont14">違反報告ありがとうございます。<br>利用規約に照らし合わせて精査いたします。</p>
        <p class="lyMb16 pFont14">※もう一度「違反報告ボタン」をクリックすると取り消しができます。</p>
        <ErrorsArea :messages="_connector.errors" />
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="isShowViolationDisableModal"
        title-text="違反報告取り消し"
        submit-text=""
        @request-close="isShowViolationDisableModal = false"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">違反報告は取り消されました。</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="isShowDeleteReadyModal"
        title-text="投稿削除"
        submit-text="削除する"
        @request-close="isShowDeleteReadyModal = false"
        @request-submit="changeDelete"
      >
        <p class="lyMb16 pFont14">投稿を削除します。よろしいですか？</p>
        <p class="lyMb16 pFont14">削除すると投稿データやコメントも削除されます。<br>データを残したい場合は、非公開設定をすればサイト上は非公開になります。</p>
        <ErrorsArea :messages="_connector.errors" />
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="isShowDeleteCompleteModal"
        title-text="投稿削除"
        submit-text=""
        @request-close="window.location.reload()"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">投稿を削除しました。</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <SnsShareModal
        :is-show="isShowSnsShareModal"
        :post-id="post.id"
        @request-close="isShowSnsShareModal = false"
      ></SnsShareModal>
    </teleport>
  </div>
</template>
